import { createContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { SSEEvent, SlackProposal } from '../api/types.js';
import { useDecideSlackProposal } from '../hooks/use-slack-proposals.js';
import { Button } from './button.js';
import { PlainText } from './plain-text.js';
import { UNSURE_HOLD_MS } from './slack-thread.js';

// A Slack message the session proposed (ADR-039 8c), decided where it was proposed: in the session's timeline, not in
// To do. What will be sent is shown raw and editable; it goes under the operator's name only on his click.

/** What the timeline shows after an event: the card of the proposal that tool call made, if any */
export const SessionCardsContext = createContext<(event: SSEEvent) => ReactNode>(() => null);

export const SLACK_PROPOSE_TOOL = 'mcp__aidev-slack__slack_propose';

/**
 * Each slack_propose call of the session paired with the proposal it made: the same text (a recorded proposal never
 * changes), the earliest one not yet paired. A refused call (a broadcast mention, a bad id) made none and gets no card;
 * a proposal whose call is not in the events read is shown apart
 */
export function pairProposals(events: readonly SSEEvent[], proposals: readonly SlackProposal[]): { byEvent: Map<number, SlackProposal>; unpaired: SlackProposal[] } {
  const left = [...proposals];
  const byEvent = new Map<number, SlackProposal>();
  for (const e of events) {
    if (String(e.data.type) !== 'tool_use' || e.data.tool !== SLACK_PROPOSE_TOOL) continue;
    const said = String((e.data.input as { text?: unknown } | undefined)?.text ?? '').trim();
    const i = left.findIndex((p) => p.text.trim() === said);
    if (i >= 0) byEvent.set(e.id, left.splice(i, 1)[0]!);
  }
  return { byEvent, unpaired: left };
}

const field = 'block w-full rounded-md px-2.5 py-1.5 text-sm font-[inherit] leading-relaxed focus:outline-none focus-visible:outline focus-visible:outline-2';
const fieldStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' };

/** Where it goes, in words: the conversation, and the thread when it answers one */
export function whereTo(p: SlackProposal): string {
  const d = p.destination;
  const conversation = !d || d.kind === 'unknown' ? 'a Slack conversation'
    : d.kind === 'im' ? `a direct message with ${d.person ?? 'someone'}`
      : d.kind === 'mpim' ? 'a group message' : `#${d.name ?? 'a channel'}`;
  if (!p.threadTs) return `In ${conversation}`;
  return `In ${conversation}, in the thread of ${d?.thread?.from ?? 'a message'}`;
}

export function SlackProposalCard({ serverId, sessionId, proposal: p, isAdmin }: { serverId: string; sessionId: string; proposal: SlackProposal; isAdmin: boolean }) {
  const decide = useDecideSlackProposal(serverId, sessionId);
  const [text, setText] = useState(p.text);
  // The whole message in view, as wrapped on this screen, up to ten lines: on a phone two sentences take five
  const box = useRef<HTMLTextAreaElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || el.offsetParent === null) return;
    const css = getComputedStyle(el);
    const frame = parseFloat(css.paddingTop) + parseFloat(css.paddingBottom) + parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth);
    const line = parseFloat(css.lineHeight) || 22;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth), line * 10 + frame)}px`;
  }, [text, width]);
  // The phone turned, the window resized: the text wraps anew
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const unsure = p.status === 'failed' && /may be there/.test(p.error ?? '');
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!unsure) return;
    setHeld(true);
    const t = setTimeout(() => setHeld(false), UNSURE_HOLD_MS);
    return () => clearTimeout(t);
  }, [unsure, p.decidedAt]);
  const open = p.status === 'pending' || p.status === 'failed';
  const sending = p.status === 'sending' || (decide.isPending && decide.variables?.send === true);

  return (
    <section aria-label="Slack message proposed by this session" className="mx-4 my-1 max-w-[72ch] rounded-md border border-l-3 px-4 py-3"
      style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-2)', borderLeftColor: open ? 'var(--color-brand)' : 'var(--color-border-2)' }}>
      <p className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Slack message to send</p>
      <p className="text-xs" style={{ color: 'var(--color-text-2)' }}>{whereTo(p)}</p>
      {p.destination?.thread && (
        <blockquote className="mt-1.5 border-l-2 pl-2.5" style={{ borderColor: 'var(--color-border-2)' }} aria-label="The thread's first message">
          <PlainText text={p.destination.thread.text} className="break-words text-xs" color="var(--color-text-2)" />
        </blockquote>
      )}

      {open && isAdmin ? (
        <form className="mt-2 space-y-2" aria-label="The message, as it will be sent"
          onSubmit={(e) => { e.preventDefault(); decide.mutate({ id: p.id, send: true, text: text.trim() }); }}>
          <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
            Message
            {/* 4000: MAX_TEXT in src/services/slack-proposals.ts */}
            <textarea ref={box} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={4000}
              className={`${field} mt-1`} style={fieldStyle} />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!text.trim() || sending || held || decide.isPending}>
              {sending ? 'Sending…' : p.status === 'failed' ? 'Send again' : 'Send on Slack'}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="max-lg:min-h-10" disabled={decide.isPending}
              onClick={() => decide.mutate({ id: p.id, send: false })}>Reject</Button>
          </div>
          <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
            Sent under your name when you click, exactly as written here{text.trim() !== p.text.trim() ? ' (you changed the session’s text)' : ''}. The session is told what you decide.
          </p>
          {p.status === 'failed' && (
            <p role="alert" className="text-xs" style={{ color: unsure ? 'var(--color-warn)' : 'var(--color-error)' }}>
              {unsure ? `Slack did not answer in time: the message may be there already. Check the conversation before sending again${held ? '; Send is held for a minute' : ''}.` : `Not sent: ${p.error ?? 'Slack refused it'}.`}
            </p>
          )}
          {decide.error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not sent: {(decide.error as Error).message}</p>}
        </form>
      ) : open ? (
        <>
          <PlainText text={p.text} className="mt-2 text-sm" />
          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Waiting for an admin of this server to send or reject it.</p>
        </>
      ) : (
        <>
          <PlainText text={p.sentText ?? p.text} className="mt-2 text-sm" color={p.status === 'sent' ? undefined : 'var(--color-text-3)'} />
          <p className="mt-1 text-xs" style={{ color: p.status === 'sent' ? 'var(--color-success)' : 'var(--color-text-3)' }}>
            {p.status === 'sending' && 'Sending…'}
            {p.status === 'sent' && <>Sent on Slack under your name.{p.permalink && <> <a href={p.permalink} target="_blank" rel="noreferrer" className="underline">Open in Slack<span className="sr-only"> (new tab)</span></a></>}</>}
            {p.status === 'rejected' && 'Rejected: nothing was sent.'}
            {p.status === 'expired' && 'Expired undecided after 7 days: nothing was sent.'}
          </p>
        </>
      )}
    </section>
  );
}
